import React from 'react';
import {
  Server,
  Users,
  GitBranch,
  Shield,
  Link2,
  TrendingUp,
  AlertTriangle,
  ChevronRight,
  Activity,
} from 'lucide-react';

function StatCard({ title, value, icon: Icon, color, onClick }) {
  return (
    <button
      onClick={onClick}
      className="card-hover p-4 text-left w-full"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-dark-300 text-sm">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon size={20} />
        </div>
      </div>
    </button>
  );
}

function EntityTypeBadge({ type }) {
  const config = {
    SERVICE: { bg: 'bg-blue-500/20', text: 'text-blue-400' },
    TEAM: { bg: 'bg-green-500/20', text: 'text-green-400' },
    PATTERN: { bg: 'bg-purple-500/20', text: 'text-purple-400' },
    API: { bg: 'bg-yellow-500/20', text: 'text-yellow-400' },
    REGULATION: { bg: 'bg-red-500/20', text: 'text-red-400' },
  };

  const { bg, text } = config[type] || { bg: 'bg-gray-500/20', text: 'text-gray-400' };

  return (
    <span className={`badge ${bg} ${text}`}>
      {type}
    </span>
  );
}

function Dashboard({ stats, entities, relationships, loading, onNavigate }) {
  const criticalServices = entities.filter(
    e => e.entity_type === 'SERVICE' && e.metadata?.criticality === 'critical'
  );

  const recentActivity = [
    { id: 1, action: 'Added', entity: 'payment-service', type: 'SERVICE', time: '2 hours ago' },
    { id: 2, action: 'Updated', entity: 'auth-service', type: 'SERVICE', time: '5 hours ago' },
    { id: 3, action: 'Created', entity: 'DEPENDS_ON', type: 'relationship', time: '1 day ago' },
    { id: 4, action: 'Added', entity: 'Circuit Breaker', type: 'PATTERN', time: '2 days ago' },
  ];

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-pulse text-dark-300">Loading...</div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="text-dark-300 mt-1">
            Overview of your organizational knowledge graph
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <StatCard
            title="Services"
            value={stats.service}
            icon={Server}
            color="bg-blue-500/20 text-blue-400"
            onClick={() => onNavigate('explore')}
          />
          <StatCard
            title="Teams"
            value={stats.team}
            icon={Users}
            color="bg-green-500/20 text-green-400"
            onClick={() => onNavigate('explore')}
          />
          <StatCard
            title="Patterns"
            value={stats.pattern}
            icon={GitBranch}
            color="bg-purple-500/20 text-purple-400"
            onClick={() => onNavigate('explore')}
          />
          <StatCard
            title="APIs"
            value={stats.api}
            icon={Activity}
            color="bg-yellow-500/20 text-yellow-400"
            onClick={() => onNavigate('explore')}
          />
          <StatCard
            title="Regulations"
            value={stats.regulation}
            icon={Shield}
            color="bg-red-500/20 text-red-400"
            onClick={() => onNavigate('explore')}
          />
          <StatCard
            title="Relationships"
            value={stats.relationships}
            icon={Link2}
            color="bg-accent-purple/20 text-accent-purple"
            onClick={() => onNavigate('explore')}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Critical Services */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <AlertTriangle size={18} className="text-red-400" />
                Critical Services
              </h2>
              <button
                onClick={() => onNavigate('explore')}
                className="text-sm text-accent-purple hover:text-accent-purple-light transition-colors flex items-center gap-1"
              >
                View All <ChevronRight size={16} />
              </button>
            </div>

            <div className="space-y-3">
              {criticalServices.length > 0 ? (
                criticalServices.map((service) => (
                  <div
                    key={service.id}
                    className="flex items-center justify-between p-3 bg-dark-700 rounded-lg hover:bg-dark-600 transition-colors cursor-pointer"
                    onClick={() => onNavigate('explore')}
                  >
                    <div className="flex items-center gap-3">
                      <Server size={16} className="text-blue-400" />
                      <div>
                        <p className="font-medium">{service.name}</p>
                        <p className="text-xs text-dark-300">{service.metadata?.team || 'Unknown Team'}</p>
                      </div>
                    </div>
                    <span className="badge bg-red-500/20 text-red-400">Critical</span>
                  </div>
                ))
              ) : (
                <p className="text-dark-400 text-sm">No critical services found</p>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <TrendingUp size={18} className="text-accent-purple" />
                Recent Activity
              </h2>
            </div>

            <div className="space-y-3">
              {recentActivity.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-center justify-between p-3 bg-dark-700 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-accent-purple"></div>
                    <div>
                      <p className="text-sm">
                        <span className="text-dark-300">{activity.action}</span>{' '}
                        <span className="font-medium">{activity.entity}</span>
                      </p>
                      <EntityTypeBadge type={activity.type} />
                    </div>
                  </div>
                  <span className="text-xs text-dark-400">{activity.time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => onNavigate('search')}
            className="card-hover p-4 text-left group"
          >
            <h3 className="font-semibold mb-2 group-hover:text-accent-purple transition-colors">
              Semantic Search
            </h3>
            <p className="text-sm text-dark-300">
              Find services, patterns, and documentation using natural language
            </p>
          </button>

          <button
            onClick={() => onNavigate('explore')}
            className="card-hover p-4 text-left group"
          >
            <h3 className="font-semibold mb-2 group-hover:text-accent-purple transition-colors">
              Explore Dependencies
            </h3>
            <p className="text-sm text-dark-300">
              Visualize how services connect and depend on each other
            </p>
          </button>

          <button
            onClick={() => onNavigate('add')}
            className="card-hover p-4 text-left group"
          >
            <h3 className="font-semibold mb-2 group-hover:text-accent-purple transition-colors">
              Add Knowledge
            </h3>
            <p className="text-sm text-dark-300">
              Import documentation and extract entities automatically
            </p>
          </button>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
